import React, { useMemo } from 'react';
import { Offer, PaymentPlace, useAnalytics, useAuthManager } from '@funtech-int/yango-shared';

import { Button } from '@/shared/components/Button/Button';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';

import { createSubscriptionOfferAnalytics } from '../../lib/subscriptionOfferAnalytics';
import { usePaywallContext } from '../../hooks/usePaywallContext';
import { useI18n } from '../../hooks/useOnboardingI18n';
import { getSubscriptionOfferButtonText } from '../../lib/getSubscriptionOfferButtonText';

import styles from './PaywallSubscriptionInset.module.css';

export const PaywallSubscriptionInset = () => {
    const { offer, paymentPlace } = usePaywallContext();

    if (!offer) {
        return null;
    }

    return <PaywallSubscriptionInsetContent offer={offer} paymentPlace={paymentPlace} />;
};

interface PaywallSubscriptionInsetContentProps {
    offer: Offer;
    paymentPlace?: PaymentPlace;
}

const PaywallSubscriptionInsetContent = ({
    offer,
    paymentPlace,
}: PaywallSubscriptionInsetContentProps) => {
    const { isAuthorized } = useAuthManager();
    const i18n = useI18n();

    const buttonText = getSubscriptionOfferButtonText(
        i18n,
        offer.offerTexts.buttonText,
        !isAuthorized
    );

    const analytics = useAnalytics((eventSender) =>
        createSubscriptionOfferAnalytics(eventSender, {
            buttonText,
            offer,
        })
    );

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();
    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const controller = useMemo(() => {
        function onPurchaseSuccess() {
            subscriptionRefetch();
            analytics.succeeded();
            offerWebAnalytics.purchaseSucceeded();
        }
        return {
            onOfferShow: () => {
                analytics.showed();
                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
            },
            onOfferClick: async () => {
                analytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentPlace?.analytics.offerClicked(offer);

                await paymentPlace?.checkout(offer, {
                    onPurchaseSuccess,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [offer, offerLoginUrl, offerWebAnalytics, paymentPlace, analytics, subscriptionRefetch]);

    const additionText = offer.offerTexts?.buttonTextWithDetails;
    const buttonVariant = isAuthorized ? 'plus' : 'accent';
    const shouldShowAdditionText = Boolean(additionText && isAuthorized);

    return (
        <div className={styles.root}>
            <Button
                className={styles.button}
                size={60}
                variant={buttonVariant}
                onClick={controller.onOfferClick}
                data-test-id="SubscriptionPurchaseButton"
                onShow={controller.onOfferShow}
            >
                {buttonText}
            </Button>

            {shouldShowAdditionText && <p className={styles.purchaseButtonText}>{additionText}</p>}
        </div>
    );
};
